import { Link } from '@tanstack/react-router';
import { IoSettingsOutline, IoLogOutOutline } from "react-icons/io5";
import { useSupabase } from '../../../contexts/SupabaseContext';
import { useUsers } from '../../../hooks/useUsers';
import { useOrganization } from '../../../hooks/useOrganization';
import CompanyLogo from '../../common/CompanyLogo';
import { motion, AnimatePresence } from 'framer-motion';
import { plans } from '../../../data/plans';

interface LogoDropdownProps {
  isOpen: boolean;
  onClose: () => void;
}

export const LogoDropdown: React.FC<LogoDropdownProps> = ({ isOpen, onClose }) => {
  const { signOut } = useSupabase();
  const { user } = useUsers();
  const { organization } = useOrganization();

  const currentPlan = plans.find(p => p.id === organization?.current_plan);
  const planName = currentPlan?.name || 'Free Plan';

  const handleLogout = async () => {
    try {
      await signOut();
      onClose();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          className="absolute left-0 top-full mt-1 w-[260px] bg-[#FFFFFF] rounded-lg overflow-hidden z-50 shadow-md border border-[#EEEEEE]"
        >
          <div className="p-3">
            <div className="flex flex-col bg-[#FAFAFA] rounded-md border border-[#EEEEEE]">
              <div className="flex items-center gap-2 p-2">
                <CompanyLogo
                  name={organization?.name || ''}
                  website={''}
                  existingLogo={''}
                  className="w-[44px] h-[44px] rounded-md"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-left text-[12px] font-medium text-[#222222] truncate">
                    {organization?.name?.toUpperCase()}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] font-normal bg-[#E0F7FA] text-[#00796B] border border-[#C8E6C9]/50">
                      {planName}
                    </span>
                  </div>
                </div>
              </div>

              <div className="text-left mt-1 mx-2 mb-2 p-1.5 rounded-md bg-white border border-[#EEEEEE]">
                <div className="text-xs text-[#222222] font-normal truncate">
                  {user?.email}
                </div>
                <div className="flex items-center gap-1 mt-0.5">
                  <div className="w-1 h-1 rounded-full bg-[#42E56F]" />
                  <span className="text-[10px] text-[#7A7A7A]">
                    {user?.role || 'No role set'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-[#EEEEEE]">
            <div className="p-2">
              <Link
                to="/settings/plan"
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[#222222] hover:bg-[#FAFAFA] rounded-md transition-colors"
                onClick={onClose}
              >
                <IoSettingsOutline className="w-4.5 h-4.5 text-[#7A7A7A]" />
                <span>Workspace Settings</span>
              </Link>

              <button 
                onClick={handleLogout}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[#D84315] hover:bg-[#FAFAFA] rounded-md transition-colors mt-1"
              >
                <IoLogOutOutline className="w-4.5 h-4.5" />
                <span>Log out</span>
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}; 